import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { Mag } from '../components/mag.js';
import { env } from '../lib/env.js';
import { brandsQuery } from '../lib/queries.js';

// /brand/$slug/meta-connect/error — brand-aware version of the
// connect-error page (s191.3). The api redirects here when an
// OAuth failure happens AFTER state has been verified, so we know
// which brand the founder was connecting and can deep-link the
// retry / back actions to that brand.
//
// Errors that fire BEFORE state verify (invalid state, missing
// params, oauth not configured) land on /meta-connect/error
// instead since we don't know the brand context.

const searchSchema = z.object({
  detail: z.string().optional(),
});

export const Route = createFileRoute('/brand/$slug/meta-connect/error')({
  validateSearch: (s) => searchSchema.parse(s),
  component: BrandMetaConnectErrorPage,
});

function BrandMetaConnectErrorPage() {
  const { slug } = Route.useParams();
  const { detail } = Route.useSearch();
  const copy = renderErrorCopy(detail);
  const brandsQ = useQuery(brandsQuery());
  const brand = brandsQ.data?.find((b) => b.slug === slug) ?? null;
  const retryUrl = brand ? `${env.API_URL}/api/brands/${brand.id}/meta/connect/start` : null;

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <section className="mx-auto max-w-[640px] px-4 pb-16 pt-12 sm:px-7">
        <Link
          to="/brand/$slug/channels"
          params={{ slug }}
          className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink)]/55 hover:text-[var(--ink)]"
        >
          {'←'} <Trans>back to channels</Trans>
        </Link>
        <div className="mt-6 flex items-start gap-3">
          <Mag size={44} mood="default" />
          <div className="flex-1">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>connect meta</Trans>
            </p>
            <h1 className="mt-1 font-display text-[28px] font-black leading-[1.05] tracking-[-0.04em] sm:text-[34px]">
              {copy.title}
            </h1>
            <p className="mt-4 font-serif text-[16px] leading-[1.5] text-[var(--ink)]/85">
              “{copy.message}”
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to="/brand/$slug/channels" params={{ slug }} className="link-quiet text-[12px]">
                {'↘'} <Trans>open channels</Trans>
              </Link>
              {retryUrl ? (
                <a href={retryUrl} className="btn">
                  <Trans>try connecting again</Trans>
                  <span className="btn-arrow">→</span>
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function renderErrorCopy(detail?: string): { title: string; message: string } {
  switch (detail) {
    case 'state_expired':
      return {
        title: 'this took too long.',
        message: "the connect handshake timed out — try again from channels, i'll be quick.",
      };
    case 'invalid_state':
      return {
        title: 'something tampered with the link.',
        message:
          'the connect link was modified between starting and finishing. try again from a fresh tab.',
      };
    case 'oauth_not_configured':
      return {
        title: "mag isn't ready to connect yet.",
        message:
          "the meta app credentials haven't been wired on the api side. tell me to set them up and try again.",
      };
    case 'missing_params':
      return {
        title: "facebook didn't send back the right pieces.",
        message: 'the redirect was missing code or state. start over from channels.',
      };
    case 'oauth_failed':
      return {
        title: 'facebook refused.',
        message:
          "they didn't grant the scopes — most often that's because the meta app isn't approved for instagram_content_publish yet, or you declined a permission.",
      };
    default:
      return {
        title: 'something broke during connect.',
        message: detail
          ? `the api said: ${detail}. try again, or tell me about it.`
          : "i don't know exactly what — try again, or tell me about it.",
      };
  }
}
